import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';
import { REVIEW_BODY_MAX } from './recommendation-catalog.js';

/**
 * F37 — Player ↔ Trainer Recommendations: the REVIEW surface (player → trainer).
 *
 * Hybrid 1–5★ + curated tags + optional ≤1000-char text (D1); one per
 * (player, trainer, sport) while not soft-deleted (D4); updatable within 30
 * days (D4); optionally anonymous on display (D3); verification-gated (D5);
 * aggregated into Bayesian-weighted materialized counters (D2).
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export const REVIEW_STATUS_VALUES = ['published', 'flagged', 'removed'] as const;
export type ReviewStatus = (typeof REVIEW_STATUS_VALUES)[number];
export const ReviewStatusSchema = z.enum(REVIEW_STATUS_VALUES);

export const REVIEW_PROMPT_STATUS_VALUES = [
  'pending',
  'submitted',
  'dismissed',
  'expired',
] as const;
export type ReviewPromptStatus = (typeof REVIEW_PROMPT_STATUS_VALUES)[number];
export const ReviewPromptStatusSchema = z.enum(REVIEW_PROMPT_STATUS_VALUES);

/** Admin moderation actions on a review (D20). */
export const ADMIN_REVIEW_MODERATION_ACTIONS = [
  'hide',
  'remove',
  'restore',
  'edit',
  'lock',
] as const;
export type AdminReviewModerationAction = (typeof ADMIN_REVIEW_MODERATION_ACTIONS)[number];

// ─── Error codes ─────────────────────────────────────────────────────────────

export const REVIEW_ERROR_CODES = [
  'REVIEW_NOT_VERIFIED',
  'REVIEW_SELF_FORBIDDEN',
  'REVIEW_DUPLICATE',
  'REVIEW_EDIT_WINDOW_CLOSED',
  'REVIEW_LOCKED',
  'REVIEW_TAG_INVALID',
  'REVIEW_NOT_FOUND',
] as const;
export type ReviewErrorCode = (typeof REVIEW_ERROR_CODES)[number];

// ─── Input schemas ───────────────────────────────────────────────────────────

export const SubmitReviewInputSchema = z
  .object({
    trainerUserId: z.string().uuid(),
    sportName: SportNameSchema,
    rating: z.number().int().min(1).max(5),
    tags: z.array(z.string().min(1).max(60)).max(20).optional(),
    body: z.string().max(REVIEW_BODY_MAX).optional(),
    anonymous: z.boolean().optional(),
    /**
     * Optional explicit proving-booking id. When omitted the service resolves
     * the most recent completed trainer session for (player, trainer, sport).
     */
    sessionBookingId: z.string().uuid().optional(),
  })
  .strict();
export type SubmitReviewInput = z.infer<typeof SubmitReviewInputSchema>;

export const EditReviewInputSchema = z
  .object({
    rating: z.number().int().min(1).max(5).optional(),
    tags: z.array(z.string().min(1).max(60)).max(20).optional(),
    body: z.string().max(REVIEW_BODY_MAX).nullable().optional(),
    anonymous: z.boolean().optional(),
  })
  .strict();
export type EditReviewInput = z.infer<typeof EditReviewInputSchema>;

export const FlagReviewInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type FlagReviewInput = z.infer<typeof FlagReviewInputSchema>;

export const AdminModerateReviewInputSchema = z
  .object({
    action: z.enum(ADMIN_REVIEW_MODERATION_ACTIONS),
    reason: z.string().min(1).max(500),
    /** Only honoured when action='edit'. */
    body: z.string().max(REVIEW_BODY_MAX).nullable().optional(),
  })
  .strict();
export type AdminModerateReviewInput = z.infer<typeof AdminModerateReviewInputSchema>;

// ─── Read shapes ─────────────────────────────────────────────────────────────

/**
 * Public review row. Anonymity is APPLIED — when the row is `anonymous`, the
 * author name is masked and `playerUserId` is omitted. F09 v2 (E32) renames the
 * mask label to "Anonymous player" (`recommendation:review.anonymous_player`) —
 * "Verified" now only ever means the blue tick.
 */
export type TrainerReviewPublic = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly sportName: SportName;
  readonly rating: number;
  readonly tags: readonly string[];
  readonly body: string | null;
  readonly anonymous: boolean;
  /** Masked display name; "Anonymous player" when anonymous (F09 v2 — was "Verified player"). */
  readonly authorDisplayName: string;
  /** Present only when NOT anonymous. */
  readonly playerUserId: string | null;
  /**
   * F09 v2 (E32) — the author's PLAYER tick. ALWAYS false for anonymous
   * reviews.
   */
  readonly authorVerified: boolean;
  readonly createdAt: string;
  readonly lastEditedAt: string;
};

/** Admin moderation row — always carries the TRUE identity (D3). */
export type TrainerReviewAdmin = {
  readonly id: string;
  readonly playerUserId: string;
  readonly playerDisplayName: string;
  readonly trainerUserId: string;
  readonly trainerDisplayName: string;
  /** F09 v2 — the reviewed trainer's tick. */
  readonly trainerVerified: boolean;
  /**
   * F09 v2 — the TRUE author's player tick. Admins always see the real identity
   * (F37 D3), so this is set even for anonymous reviews; the public
   * `TrainerReviewPublic.authorVerified` stays false for anonymous ones (E32).
   */
  readonly authorVerified: boolean;
  readonly sportName: SportName;
  readonly sessionBookingId: string | null;
  readonly rating: number;
  readonly tags: readonly string[];
  readonly body: string | null;
  readonly anonymous: boolean;
  readonly status: ReviewStatus;
  readonly flaggedAt: string | null;
  readonly flaggedByUserId: string | null;
  readonly flagReason: string | null;
  readonly removedByAdminId: string | null;
  readonly removedReason: string | null;
  readonly adminLocked: boolean;
  readonly lastEditedAt: string;
  readonly editWindowEndsAt: string;
  readonly deletedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Per-star distribution for the profile star-bars. */
export type ReviewStarDistribution = {
  readonly count1: number;
  readonly count2: number;
  readonly count3: number;
  readonly count4: number;
  readonly count5: number;
};

export type RatingAggregateRow = {
  readonly sportName: SportName | '__overall__';
  readonly avg: number;
  readonly bayesian: number;
  readonly count: number;
  readonly distribution: ReviewStarDistribution;
  /** Ordered `[tagCode, count]` pairs, descending — the profile tag-cloud. */
  readonly topTags: ReadonlyArray<readonly [string, number]>;
};

/** The full trainer rating summary (D2): overall sentinel + per-sport rows. */
export type TrainerRatingSummary = {
  readonly overall: RatingAggregateRow;
  readonly perSport: readonly RatingAggregateRow[];
};

export type ReviewPrompt = {
  readonly id: string;
  readonly bookingId: string;
  readonly playerUserId: string;
  readonly trainerUserId: string;
  /** F09 v2 — the trainer's tick, for the prompt/composer header. */
  readonly trainerVerified: boolean;
  readonly sportName: SportName;
  readonly status: ReviewPromptStatus;
  readonly createdAt: string;
  readonly decidedAt: string | null;
  readonly expiresAt: string;
};
